import BigNumber from 'bignumber.js';
import { ITransaction } from 'store/types';
import { EmailMultisigSettings, EmailUserSettings } from 'types';

import { IContact } from '../contacts';

export interface StacksTxFeeEstimation {
  cost_scalar_change_by_byte: number;
  estimated_cost: object;
  estimated_cost_scalar: number;
  estimations: Estimation[];
  error?: string;
}

export interface Money {
  readonly amount: BigNumber;
  readonly symbol: string;
  readonly decimals: number;
}

export type PermissionType = 'VOTE' | 'PROPOSE' | 'EXECUTE';

export interface Permission {
  address: string;
  userId: string;
  isOwner: boolean;
  permissions: PermissionType[];
}

export interface StacksFeeEstimate {
  fee: Money;
  feeRate: number;
}

type Estimation = {
  fee: number;
  fee_rate: number;
};

export interface ICreateClaimMultiSignWallet {
  name: string;
  threshold: number;
  owners: ICreateClaimOwner[];
  network: StxChain;
}

export interface CreateClaimResponse {
  success: boolean;
  message?: string;
  invalidOwners?: IClaimOwner[];
  data?: {
    claim: IClaimMultiSignWallet;
    wallet: IMultiSignWallet;
  };
}

export interface ContactsResponse {
  success: boolean;
  data: IContact[];
}

export interface IAddContact {
  contact_name: string;
  contact_address: string;
}

export interface RefreshResponse {
  success: boolean;
  message?: string;
  data?: {
    jwtToken: string;
    mainNetAddress: string;
    testNetAddress: string;
    publicKey: string;
    refreshToken: {
      refreshToken: string;
    };
  };
}

export interface SingleWalletResponse {
  success: boolean;
  data: IMultiSignWallet;
}

export interface SingleClaimResponse {
  success: boolean;
  message?: string;
  data?: IClaimMultiSignWallet;
}

export interface PendingSafesResponse {
  success: boolean;
  data: IClaimMultiSignWallet[];
}

enum ClaimMultiSigStatus {
  QUEUED = 'QUEUED',
  SUBMITTED = 'SUBMITTED',
}

export enum StxChain {
  STX_TESTNET_CHAIN = 'stx:testnet',
  STX_MAINNET_CHAIN = 'stx:mainnet',
}

interface ICreateClaimOwner {
  address: string;
}

interface IClaimOwner extends ICreateClaimOwner {
  verified: boolean;
}

interface IBaseClaimMultiSignWallet extends ICreateClaimMultiSignWallet {
  owners: IClaimOwner[];
  ownersHash: string;
  create_time: Date;
  update_time: Date;
  creatorId: string;
  creatorAddress: string;
  status: ClaimMultiSigStatus;
  multiSigId?: string;
  address: string;
  parrentId?: string;
}

export interface IClaimMultiSignWallet extends IBaseClaimMultiSignWallet {
  _id: string;
}

interface ICreateOwner {
  userId: string;
}

export interface IOwner extends ICreateOwner {
  address: string;
  publicKey: string;
  archived?: boolean;
}

interface ICreateMultiSignWallet {
  name: string;
  threshold: number;
  owners: ICreateOwner[];
  creatorId: string;
  creatorAddress: string;
  network: StxChain;
  private?: boolean;
}

interface IBaseMultiSignWallet extends ICreateMultiSignWallet {
  owners: IOwner[];
  claimId: string;
  address: string;
  create_time: Date;
  update_time: Date;
}

type MultisignWallet = IBaseMultiSignWallet & {
  output: string;
  address: string;
  witness: string[];
  redeem: {
    output: string;
    redeemVersion: number;
  };
};

export interface IMultiSignWallet extends MultisignWallet {
  _id: string;
  actualSubscriptionEndTime: Date;
  actualSubscriptionStartTime: Date;
  parrentId?: string;
  permissions: Permission[];
}

export interface CreateNewFlowResponse {
  success: boolean;
  data: {
    transaction: ITransaction;
  };
}

export interface EmailMultisigSettingsResponse {
  success: boolean;
  data?: {
    multisig: EmailMultisigSettings;
    user?: EmailUserSettings;
  };
}

interface IUserSignature {
  userId: string;
  userAddress: string;
  signature: string;
}

export type PermissionEditType = 'EDIT' | 'DELETE';

export interface IEditWalletPermissions {
  address: string;
  processType: PermissionEditType;
  permissions: PermissionType[];
}

export interface ICreateEditPermission {
  vault: string;
  message: string;
  permissions: IEditWalletPermissions[];
}

export interface ICreateUserPermissionsSignature {
  vault: string;
  message: string;
  signature: string;
}

export interface IBaseEditPermission extends ICreateEditPermission {
  creator: string;
  creatorId: string;
  vaultId: string;
  create_time: Date;
  update_time: Date;
  signatures: IUserSignature[];
  exported: boolean;
}

export interface IEditPermission extends IBaseEditPermission {
  _id: string;
}

export interface PermissionsResponse {
  success: boolean;
  data: IEditPermission[];
}
export interface PermissionResponse {
  success: boolean;
  data: IEditPermission;
}
